// src/components/modal/SolicitudDetallesModal.jsx
import { useState, useEffect } from "react";
import { doc, updateDoc, arrayUnion, onSnapshot } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebaseconfig";
import { generarCreditoPDF } from "../reports/PDFCredito";
import logo2Url from "../../assets/logo2.png";
import uploadService from "../../services/uploadService";
import { FileText, Download, Upload, MessageSquare, CheckCircle, XCircle, Clock, AlertTriangle, Paperclip, FolderOpen, Pencil, Save, X } from "lucide-react";

export default function SolicitudDetallesModal({
  solicitud,
  isAdmin = false,
  onClose,
}) {
  // estado vivo del doc (para renderizar todo desde acá)
  const [docLive, setDocLive] = useState(solicitud);
  const [comentarios, setComentarios] = useState("");
  const [historialComentarios, setHistorialComentarios] = useState([]);
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);

  const formatCurrency = (n) =>
    new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 0 }).format(n || 0);
  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return "N/A";
    }
  };
  const tipoLegible = (t) => {
    const s = String(t || "").toLowerCase();
    if (s.includes("hipotec")) return "Hipotecario";
    if (s.includes("prend")) return "Prendario";
    if (s.includes("person")) return "Personal";
    return "—";
  };
  const estadoInfo = (estado) => {
    const e = (estado || "").toLowerCase();
    if (e === "aprobada")  return { icon: <CheckCircle size={44} color="#10b981" />, titulo: "Aprobada",    sub: "Crédito aprobado y vigente.",              cls: "ok" };
    if (e === "rechazada") return { icon: <XCircle size={44} color="#ef4444" />,     titulo: "Rechazada",   sub: "La solicitud fue rechazada.",               cls: "bad" };
    if (e === "revision")  return { icon: <Clock size={44} color="#f59e0b" />,        titulo: "En revisión", sub: "Estamos evaluando la solicitud.",           cls: "warn" };
    return                        { icon: <Clock size={44} color="#9ca3af" />,        titulo: "Pendiente",   sub: "La solicitud está pendiente de revisión.", cls: "pending" };
  };
  const S = docLive || solicitud; // fuente única de verdad para mostrar datos
  const EST = estadoInfo(S?.estado);

function inferColeccion(solic) {
  // 1) si viene explícito, úsalo
  if (solic?.coleccion) return solic.coleccion;
  if (solic?.collectionName) return solic.collectionName;

  // 2) intentá por tipo
  const t = String(solic?.tipo || "").toLowerCase().trim();
  if (t.includes("hipotec")) return "creditosHipotecarios";
  if (t.includes("prend"))   return "creditosPrendarios";
  if (t.includes("person"))  return "creditosPersonales";

  // 3) fallback para solicitudes en revisión
  return "solicitudes";
}

  // Suscripción en tiempo real al documento para REFRESCAR historial automáticamente
  useEffect(() => {
    if (!solicitud?.id) return;
    const col = inferColeccion(solicitud);
    if (!col) return;

    const refDoc = doc(db, col, String(solicitud.id));
    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const lista = Array.isArray(data.comentariosHistorial) ? data.comentariosHistorial.slice() : [];
      // ordenar por fecha desc
      lista.sort((a, b) => new Date(b.fechaCreacion || b.fecha || 0) - new Date(a.fechaCreacion || a.fecha || 0));
      setHistorialComentarios(lista);
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitud?.id, solicitud?.tipo]);

  // Cargar logo en base64 (para PDF)
  useEffect(() => {
    const toDataURL = async (url) => {
      const res = await fetch(url);
      const blob = await res.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };
    toDataURL(logo2Url).then(setLogoBase64).catch(() => setLogoBase64(null));
  }, []);

  // Guardar nuevo comentario (ADMIN) con arrayUnion
  const guardarComentario = async () => {
    if (!isAdmin) return;
    const texto = comentarios.trim();
    if (!texto) return;

    try {
      setSaving(true);
      const col = inferColeccion(solicitud);
      const id = String(solicitud?.id || "").trim();
      if (!col || !id) {
        alert("❌ No se pudo determinar dónde guardar el comentario.");
        return;
      }

      const nuevoComentario = {
        id: Date.now().toString(),
        texto,
        fechaCreacion: new Date().toISOString(),
        autor: "Admin",
        archivosAdjuntos: []
      };

      await updateDoc(doc(db, col, id), {
        comentariosHistorial: arrayUnion(nuevoComentario),
        ultimoComentario: texto,
        fechaUltimoComentario: new Date().toISOString(),
      });

      setComentarios("");
      // No hace falta forzar setHistorialComentarios: onSnapshot lo trae y reordena
    } catch (e) {
      console.error("Error guardando comentario:", e);
      alert("❌ Error guardando comentario");
    } finally {
      setSaving(false);
    }
  };

  // ---- Archivos (idéntico a tu versión actual) ----
  const mapearTipoAPath = (tipo) => {
    const m = { reciboSueldo: "recibos-sueldo", fotoDNI: "fotos-dni", escrituras: "escrituras" };
    return m[tipo] || tipo;
  };
  const obtenerPrefijoRuta = (tipoCredito) => {
    const t = String(tipoCredito || "").toLowerCase().trim();
    if (t.includes("hipotec")) return "credito-hipotecario";
    if (t.includes("prend")) return "credito-prendario";
    if (t.includes("person")) return "credito-personal";
    return `credito-${t}`;
  };
  const obtenerURLDesdeStorage = async (storageRef) => {
    try {
      const storageReference = ref(storage, storageRef);
      return await getDownloadURL(storageReference);
    } catch {
      const bucket = "gamaan-fbc65.appspot.com";
      const encoded = encodeURIComponent(storageRef);
      return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encoded}?alt=media`;
    }
  };
  const getArchivosOriginales = () => {
    const archivos = [];
    if (solicitud?.archivos) {
      Object.entries(solicitud.archivos).forEach(([tipo, info]) => {
        if (tipo.endsWith("URLs") || tipo.includes("URL")) return;
        let nombres = [];
        let urls = [];
        if (Array.isArray(info)) {
          nombres = info;
          urls = solicitud.archivos[`${tipo}URLs`] || [];
        } else if (info && typeof info === "object" && Array.isArray(info.nombres)) {
          nombres = info.nombres;
          urls = info.urls || info.urlsSubidos || info[`${tipo}URLs`] || [];
        }
        nombres.forEach((nombre, i) => {
          archivos.push({
            nombre,
            tipo,
            esOriginal: true,
            categoria: "Documento original",
            url: urls[i] || null,
            storageRef: `${obtenerPrefijoRuta(solicitud.tipo)}/${mapearTipoAPath(tipo)}/${nombre}`,
          });
        });
      });
    }
    return archivos;
  };
  const getArchivosAdicionales = () => {
    const archivos = [];
    if (Array.isArray(solicitud?.archivosAdicionales)) {
      solicitud.archivosAdicionales.forEach((a) => {
        archivos.push({
          nombre: a.nombre,
          tipo: a.tipo || "Archivo adicional",
          esOriginal: false,
          categoria: "Archivo adicional",
          url: a.url,
          fechaSubida: a.fechaSubida,
          tamano: a.tamano,
        });
      });
    }
    return archivos;
  };
  const descargarArchivoOriginal = async (archivo) => {
    try {
      let url = archivo.url;
      if (!url && archivo.storageRef) url = await obtenerURLDesdeStorage(archivo.storageRef);
      if (!url) return alert(`No se encontró URL para ${archivo.nombre}`);
      
      if (url.startsWith("data:")) {
        // Data URL - convertir a blob y descargar
        const r = await fetch(url);
        const blob = await r.blob();
        const obj = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = obj;
        a.download = archivo.nombre;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(obj);
        document.body.removeChild(a);
      } else if (url.startsWith("blob:")) {
        // Blob URL - descargar directamente
        const a = document.createElement("a");
        a.href = url;
        a.download = archivo.nombre;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // HTTP URL - abrir en nueva pestaña
        window.open(url, "_blank");
      }
    } catch (e) {
      alert(`❌ Error al descargar: ${e.message}`);
    }
  };
  const handleFileChange = (files) => {
    if (!files?.length) return;
    const allowed = ['application/pdf','image/jpeg','image/png','image/jpg','text/plain','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const valid = [];
    for (let f of files) {
      if (!allowed.includes(f.type)) { alert(`Archivo inválido: ${f.name}`); continue; }
      if (f.size > 10 * 1024 * 1024) { alert(`Muy grande: ${f.name}`); continue; }
      valid.push(f);
    }
    if (valid.length) setArchivosAdjuntos((p) => [...p, ...valid]);
  };
  const subirArchivos = async () => {
    if (!archivosAdjuntos.length) return;
    try {
      setUploading(true);
      const col = inferColeccion(solicitud);
      const id = String(solicitud?.id || "").trim();
      if (!col || !id) return;

      const ruta = `solicitudes/${id}/archivos-adicionales`;
      const r = await uploadService.subirMultiplesArchivos(archivosAdjuntos, ruta);
      if (r.exitosos?.length) {
        const nuevos = r.exitosos.map((a) => ({
          nombre: a.nombre || "archivo",
          url: a.url || null,
          fechaSubida: new Date().toISOString(),
          tipo: a.tipo,
          tamano: a.tamano,
        }));
        const actuales = solicitud.archivosAdicionales || [];
        await updateDoc(doc(db, col, id), {
          archivosAdicionales: [...actuales, ...nuevos],
          fechaUltimaActualizacion: new Date().toISOString(),
        });
        setArchivosAdjuntos([]);
      }
    } catch (e) {
      alert("❌ Error al subir archivos");
    } finally {
      setUploading(false);
    }
  };

  // === Edición solo admin ===
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({
    nombre: solicitud?.nombre || "",
    dni: solicitud?.dni || "",
    email: solicitud?.email || "",
    telefono: solicitud?.telefono || "",
    ingresos: solicitud?.ingresos ?? 0,
    observaciones: solicitud?.observaciones || "",
    tasaAnual: solicitud?.tasaAnual ?? "",
    plazo: solicitud?.plazo ?? "",
    montoSolicitado: solicitud?.montoSolicitado ?? "",
  }));

  useEffect(() => {
    if (!solicitud?.id) return;
    const col = inferColeccion(solicitud);
    if (!col) return;

    const refDoc = doc(db, col, String(solicitud.id));
    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};

      setDocLive(prev => ({
        ...(prev || {}),
        ...data,
        id: solicitud.id,
        tipo: solicitud.tipo,
        coleccion: col,
      }));

      const lista = Array.isArray(data.comentariosHistorial) ? [...data.comentariosHistorial] : [];
      lista.sort((a, b) => new Date(b.fechaCreacion || b.fecha || 0) - new Date(a.fechaCreacion || a.fecha || 0));
      setHistorialComentarios(lista);

      if (!isEditing) {
        setDraft(d => ({
          ...d,
          nombre: data.nombre || "",
          dni: data.dni || "",
          email: data.email || "",
          telefono: data.telefono || "",
          ingresos: data.ingresos ?? 0,
          observaciones: data.observaciones || "",
          tasaAnual: data.tasaAnual ?? "",
          plazo: data.plazo ?? "",
          montoSolicitado: data.montoSolicitado ?? "",
        }));
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitud?.id, solicitud?.tipo, isEditing]);

  if (!solicitud) return null;

  const archivosOriginales = getArchivosOriginales();
  const archivosAdicionales = getArchivosAdicionales();
  const comentariosParaPDF = historialComentarios;


const handleDraft = (field, value) =>
  setDraft(prev => ({ ...prev, [field]: value }));

const handleGuardarEdicion = async () => {
  try {
    const col = inferColeccion(solicitud);     // <- ahora devuelve 'solicitudes'
    const id  = String(solicitud?.id || "").trim();
    if (!col || !id) {
      alert("❌ No se pudo determinar dónde guardar.");
      return;
    }

    // Parseos numéricos seguros
    const monto = draft.montoSolicitado === "" ? null : Number(draft.montoSolicitado);
    const tasaA = draft.tasaAnual        === "" ? null : Number(draft.tasaAnual);
    const plazo = draft.plazo            === "" ? null : Number(draft.plazo);

    // Cálculo sistema francés (si hay datos)
    let cuotaCalculada = null;
    let totalAPagar    = null;
    if (monto && tasaA && plazo) {
      const r = (tasaA / 100) / 12;
      const n = plazo;
      const cuota = (monto * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      cuotaCalculada = Math.round(cuota);
      totalAPagar    = Math.round(cuota * n);
    }

    await updateDoc(doc(db, col, id), {
      nombre: draft.nombre,
      dni: draft.dni,
      email: draft.email,
      telefono: draft.telefono,
      ingresos: Number(draft.ingresos) || 0,
      observaciones: draft.observaciones,
      tasaAnual: tasaA,
      plazo: plazo,
      montoSolicitado: monto,
      ...(cuotaCalculada !== null ? { cuotaCalculada } : {}),
      ...(totalAPagar    !== null ? { totalAPagar }    : {}),
      fechaUltimaActualizacion: new Date().toISOString(),
    });

    setDocLive(prev => prev ? {
  ...prev,
  nombre: draft.nombre,
  dni: draft.dni,
  email: draft.email,
  telefono: draft.telefono,
  ingresos: Number(draft.ingresos) || 0,
  observaciones: draft.observaciones,
  tasaAnual: tasaA,
  plazo,
  montoSolicitado: monto,
  ...(cuotaCalculada !== null ? { cuotaCalculada } : {}),
  ...(totalAPagar    !== null ? { totalAPagar }    : {}),
} : prev);

    setIsEditing(false);
    alert("✅ Cambios guardados");
    // No hace falta forzar setDocLive: onSnapshot ya refresca todo
  } catch (e) {
    console.error(e);
    alert("❌ Error guardando cambios");
  }
};





  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="dlg-header">
          <div>
            <h2 className="dlg-title">Detalles de la solicitud</h2>
            <p className="dlg-subtitle">Información completa de la solicitud.</p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="dlg-grid">
          <aside className="dlg-col-side">
            <div className={`card estado-card estado-${EST.cls}`}>
              <div className="card-header">Estado actual</div>
              <div className="card-body center">
                <div className="estado-icon">{EST.icon}</div>
                <div className="estado-titulo">{EST.titulo}</div>
                <div className="estado-sub">{EST.sub}</div>
{isAdmin && (
                <div className="stack gap" style={{ marginTop: 10 }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => generarCreditoPDF(S, { comentarios: comentariosParaPDF, logo: logoBase64 })}
                  >
                    <FileText size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Generar PDF
                  </button>
                </div>
              )}

              {isAdmin && (
  <div className="stack gap" style={{ marginTop: 8 }}>
    {!isEditing ? (
      <button className="btn btn-ghost" onClick={() => setIsEditing(true)}>
        <Pencil size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Editar datos
      </button>
    ) : (
      <div className="stack gap">
        <button className="btn btn-primary" onClick={handleGuardarEdicion}>
          <Save size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Guardar cambios
        </button>
        <button className="btn btn-ghost" onClick={() => setIsEditing(false)}>
          <X size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Cancelar
        </button>
      </div>
    )}
  </div>
)}

              </div>
            </div>

            <div className="card">
              <div className="card-header">Información adicional</div>
              <div className="card-body">
                <div className="kv"><span className="k">Estado</span><span className="v">{EST.titulo}</span></div>
                <div className="kv"><span className="k">Estado de pago</span><span className="v">{S.estadoPago || "—"}</span></div>
                <div className="kv"><span className="k">Fecha aprobación</span><span className="v">{S.fechaAprobacion ? formatDate(S.fechaAprobacion) : "—"}</span></div>
                <div className="kv"><span className="k">Asignado a</span><span className="v">{S.agenciaUsuario?.nombre || "—"}</span></div>
              </div>
            </div>

            <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
          </aside>

          <main className="dlg-col-main">
            <div className="card">
  <div className="card-header">Información del solicitante</div>
  <div className="card-body ">
    {!isEditing ? (
      <>
        <div className="kv"><span className="k">Nombre completo</span><span className="v">{S.nombre || "—"}</span></div>
        <div className="kv"><span className="k">DNI</span><span className="v">{S.dni || "—"}</span></div>
        <div className="kv"><span className="k">Teléfono</span><span className="v">{S.telefono || "—"}</span></div>
        <div className="kv"><span className="k">Email</span><span className="v">{S.email || "—"}</span></div>
        <div className="kv"><span className="k">Ingresos</span><span className="v">{formatCurrency(S.ingresos)}</span></div>
        <div className="kv"><span className="k">Fecha de solicitud</span><span className="v">{formatDate(S.fechaCreacion)}</span></div>
      </>
    ) : (
      <>
        <div className="kv">
          <span className="k">Nombre completo</span>
          <input className="text-input" value={draft.nombre} onChange={e => handleDraft("nombre", e.target.value)} />
        </div>
        <div className="kv">
          <span className="k">DNI</span>
          <input className="text-input" value={draft.dni} onChange={e => handleDraft("dni", e.target.value)} />
        </div>
        <div className="kv">
          <span className="k">Teléfono</span>
          <input className="text-input" value={draft.telefono} onChange={e => handleDraft("telefono", e.target.value)} />
        </div>
        <div className="kv">
          <span className="k">Email</span>
          <input className="text-input" value={draft.email} onChange={e => handleDraft("email", e.target.value)} />
        </div>
        <div className="kv">
          <span className="k">Ingresos</span>
          <input type="number" className="text-input" value={draft.ingresos} onChange={e => handleDraft("ingresos", e.target.value)} />
        </div>
        <div className="kv">
          <span className="k">Observaciones</span>
          <textarea className="text-area" rows={3} value={draft.observaciones} onChange={e => handleDraft("observaciones", e.target.value)} />
        </div>
      </>
    )}
  </div>
</div>


     <div className="card">
  <div className="card-header">Detalles del préstamo</div>
  <div className="card-body ">
    <div className="kv"><span className="k">Tipo</span><span className="v">{tipoLegible(S.tipo)}</span></div>

    {!isEditing ? (
      <>
        <div className="kv"><span className="k">Monto solicitado</span><span className="v">{formatCurrency(S.montoSolicitado)}</span></div>
        <div className="kv"><span className="k">Plazo</span><span className="v">{(S.plazo || 0)} {S.tipo === "hipotecario" ? "años" : "meses"}</span></div>
        <div className="kv"><span className="k">Tasa anual</span><span className="v">{S.tasaAnual ? `${S.tasaAnual}%` : "—"}</span></div>
      </>
    ) : (
      <>
        <div className="kv">
          <span className="k">Monto solicitado</span>
          <input type="number" className="text-input" value={draft.montoSolicitado} onChange={e => handleDraft("montoSolicitado", e.target.value)} />
        </div>
        <div className="kv">
          <span className="k">Plazo</span>
          <input type="number" className="text-input" value={draft.plazo} onChange={e => handleDraft("plazo", e.target.value)} />
        </div>
        <div className="kv">
          <span className="k">Tasa anual</span>
          <input type="number" className="text-input" value={draft.tasaAnual} onChange={e => handleDraft("tasaAnual", e.target.value)} />
        </div>
      </>
    )}

<div className="kv">
  <span className="k">Cuota mensual</span>
  <span className="v">{formatCurrency(S.cuotaCalculada)}</span>
</div>

{isAdmin && (
  <div className="kv">
    <span className="k">Total a pagar</span>
    <span className="v">{formatCurrency(S.totalAPagar)}</span>
  </div>
)}

  </div>
</div>


            {/* Documentación */}
            <div className="card">
              <div className="card-header">Documentación</div>
              <div className="card-body">
                {getSectionDocs("Documentos originales", archivosOriginales, descargarArchivoOriginal)}
                {getSectionDocs("Archivos adicionales", archivosAdicionales, descargarArchivoOriginal, true)}

                <div className="file-upload-section">
                  <h4 className="doc-section-title">Subir archivos</h4>
                  {archivosAdjuntos.length > 0 && (
                    <div className="archivos-adjuntos mb-3">
                      <h5>Archivos seleccionados ({archivosAdjuntos.length}):</h5>
                      {archivosAdjuntos.map((a, i) => (
                        <div key={i} className="archivo-adjunto">
                          <span className="archivo-nombre">{a.name || a.nombre} <small className="file-size">({(a.size / 1024 / 1024).toFixed(2)} MB)</small></span>
                          <button type="button" onClick={() => setArchivosAdjuntos((p) => p.filter((_, idx) => idx !== i))} className="btn-remove-file">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="upload-controls">
                    <label htmlFor="file-upload-solicitud" className="file-upload-label">
                      <FolderOpen size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Seleccionar archivos
                    </label>
                    <input id="file-upload-solicitud" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.txt,.doc,.docx" onChange={(e) => handleFileChange(e.target.files)} className="file-upload-input" />
                    {archivosAdjuntos.length > 0 && (
                      <button className="btn btn-primary upload-btn" onClick={subirArchivos} disabled={uploading}>
                        <Upload size={15} style={{ marginRight: 6, flexShrink: 0 }} />
                        {uploading ? "Subiendo..." : `Subir ${archivosAdjuntos.length} archivo${archivosAdjuntos.length > 1 ? "s" : ""}`}
                      </button>
                    )}
                  </div>
                  <small className="file-upload-help">PDF, JPG, PNG, TXT, DOC, DOCX (máx. 10MB c/u)</small>
                </div>
              </div>
            </div>

            {/* Historial y alta de comentarios: SOLO ADMIN */}
            {isAdmin && (
              <>
                <div className="card">
                  <div className="card-header">Historial de comentarios</div>
                  <div className="card-body">
                    {historialComentarios.length ? (
                      <div className="comentarios-historial">
                        {historialComentarios.map((c) => (
                          <div key={c.id} className="comentario-item">
                            <div className="comentario-header">
                              <span className="comentario-autor">{c.autor || "Admin"}</span>
                              <span className="comentario-fecha">{formatDate(c.fechaCreacion || c.fecha)}</span>
                            </div>
                            <div className="comentario-texto">{c.texto}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="muted">No hay comentarios aún.</div>
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">Agregar comentario</div>
                  <div className="card-body">
                    <textarea
                      className="text-area"
                      rows={4}
                      placeholder="Escribe tu comentario aquí..."
                      value={comentarios}
                      onChange={(e) => setComentarios(e.target.value)}
                    />
                    <div className="actions-right">
                      <button className="btn btn-primary" disabled={saving || !comentarios.trim()} onClick={guardarComentario}>
                        {saving ? "Guardando..." : <><MessageSquare size={15} style={{ marginRight: 6, flexShrink: 0 }} /> Enviar comentario</>}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );

  function getSectionDocs(titulo, lista, onClick, showDate = false) {
    if (!lista.length) return null;
    return (
      <div className="mb-4">
        <h4 className="doc-section-title">{titulo}</h4>
        <ul className="docs-list">
          {lista.map((d, i) => (
            <li key={i} className="doc-item">
              <div className="doc-icon"><FileText size={18} /></div>
              <div className="doc-info">
                <div className="doc-name">{d.nombre}</div>
                <div className="doc-size">
                  {d.tipo} {showDate && d.fechaSubida ? `· ${formatDate(d.fechaSubida)}` : ""}
                </div>
              </div>
              <button className="doc-download" onClick={() => onClick(d)} title="Descargar archivo"><Download size={16} /></button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
}
